import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Upload,
    message,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, DownloadOutlined, FileExcelOutlined, HistoryOutlined, PlusOutlined, ReloadOutlined, UnlockOutlined } from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import RequestState from '../../components/business/RequestState';
import StatusTag from '../../components/business/StatusTag';
import {
    RecipientImportRow,
    RecipientImportSummary,
    RecipientListMember,
    RecipientListOwner,
    RecipientListReference,
    RecipientListVersion,
} from '../../models/mail';
import { PageResult } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDateTime } from '../../utils/v2';
import { capabilityMessage, downloadEmailImportTemplate, mailFieldLabel } from './mailUi';
import CompanyService, { ICompanyInfo } from '../../service/company/CompanyService';
import CustomerService, { ICustomerInfo } from '../../service/customer/CustomerService';
import { MAIL_PAGE_SIZE_OPTIONS, readMailPageSize, rememberMailPageSize } from './mailPagination';
const emptyMembers: PageResult<RecipientListMember> = {
    items: [],
    pageNo: 1,
    pageSize: readMailPageSize('recipient-members', 20),
    total: 0,
};
const emptyImportRows: PageResult<RecipientImportRow> = {
    items: [],
    pageNo: 1,
    pageSize: readMailPageSize('recipient-import-rows', 20),
    total: 0,
};
const activeImportStates = ['UPLOADED', 'QUEUED', 'PARSING', 'MATCHING', 'VALIDATING', 'CONFIRMING', 'RUNNING'];

const recipientVersionStatusLabel = (status?: string) => {
    const labels: { [key: string]: string } = {
        DRAFT: '草稿',
        VALIDATING: '校验中',
        READY: '可编辑',
        FROZEN: '已冻结',
        FAILED: '失败',
        ARCHIVED: '已归档',
    };
    return status ? labels[status] || status : '未知状态';
};

const recipientImportStatusLabel = (status?: string) => {
    const labels: { [key: string]: string } = {
        UPLOADED: '已上传',
        QUEUED: '排队中',
        PARSING: '解析中',
        MATCHING: '匹配中',
        VALIDATING: '校验中',
        RUNNING: '处理中',
        PARSED: '解析完成',
        PRECHECKED: '预检通过',
        CONFIRMING: '导入中',
        COMMITTED: '已导入',
        PARTIAL: '部分完成',
        FAILED: '失败',
        CANCELLED: '已取消',
    };
    return status ? labels[status] || '未知导入状态' : '—';
};

const emptyCompanyPage = {
    records: [] as any[], total: 0, current: 1, size: readMailPageSize('recipient-companies', 10),
};
const emptyContactPage = {
    records: [] as any[], total: 0, current: 1, size: readMailPageSize('recipient-contacts', 10),
};

const RecipientListsPage = () => {
    const [createForm] = Form.useForm();
    const [openForm] = Form.useForm();
    const [freezeForm] = Form.useForm();
    const [resolveForm] = Form.useForm();
    const [excludeForm] = Form.useForm();
    const [versionForm] = Form.useForm();
    const [current, setCurrent] = useState<RecipientListReference | null>(null);
    const [members, setMembers] = useState(emptyMembers);
    const [memberFilters, setMemberFilters] = useState<any>({
        eligibilityStatus: 'ELIGIBLE', pageNo: 1, pageSize: emptyMembers.pageSize,
    });
    const [importSummary, setImportSummary] = useState<RecipientImportSummary | null>(null);
    const [importRows, setImportRows] = useState(emptyImportRows);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importError, setImportError] = useState<string | null>(null);
    const [createVisible, setCreateVisible] = useState(false);
    const [companyPickerVisible, setCompanyPickerVisible] = useState(false);
    const [companyPage, setCompanyPage] = useState(emptyCompanyPage);
    const [companyKeyword, setCompanyKeyword] = useState('');
    const [companyLoading, setCompanyLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<ICompanyInfo | null>(null);
    const [contactPage, setContactPage] = useState(emptyContactPage);
    const [contactLoading, setContactLoading] = useState(false);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState<React.Key[]>([]);
    const [editingMember, setEditingMember] = useState<RecipientListMember | null>(null);
    const [historyVisible, setHistoryVisible] = useState(false);
    const [createVersionVisible, setCreateVersionVisible] = useState(false);
    const [versions, setVersions] = useState<RecipientListVersion[]>([]);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [freezeVisible, setFreezeVisible] = useState(false);
    const [resolvingRow, setResolvingRow] = useState<RecipientImportRow | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<V2ApiError | null>(null);
    const [ownerOptions, setOwnerOptions] = useState<RecipientListOwner[]>([]);
    const [loadingOwners, setLoadingOwners] = useState(false);
    const [ownerError, setOwnerError] = useState<string>();
    const [canViewAllLists, setCanViewAllLists] = useState(false);
    const [currentActorId, setCurrentActorId] = useState<number>();
    const [listOptions, setListOptions] = useState<RecipientListReference[]>([]);
    const [loadingLists, setLoadingLists] = useState(false);
    const listSearchTimer = useRef<number>();

    const loadMembers = useCallback(
        async (target: RecipientListReference, filters: any) => {
            try {
                setMembers(
                    await v2Api.recipientListMembers(target.listId, target.versionId, filters)
                );
            } catch (nextError) {
                setError(asV2Error(nextError));
            }
        },
        []
    );

    const openList = useCallback(async (target: RecipientListReference, targetImportId?: number) => {
        setLoading(true);
        setError(null);
        setImportSummary(null);
        setImportRows(emptyImportRows);
        try {
            const detail = await v2Api.recipientList(target.listId, target.versionId);
            const next = { ...target, ...detail };
            setCurrent(next);
            if (targetImportId) {
                try {
                    const imported = await v2Api.recipientImport(
                        target.listId,
                        target.versionId,
                        targetImportId
                    );
                    setImportSummary(imported);
                    if (!activeImportStates.includes(imported.status)) {
                        setImportRows(
                            await v2Api.recipientImportRows(
                                target.listId,
                                target.versionId,
                                targetImportId,
                                { pageNo: 1, pageSize: emptyImportRows.pageSize }
                            )
                        );
                    }
                } catch (nextError) {
                    setImportError(capabilityMessage(asV2Error(nextError), 'Excel/CSV 导入'));
                }
            }
        } catch (nextError) {
            setError(asV2Error(nextError));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const query = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const listId = Number(query.get('listId'));
        const versionId = Number(query.get('versionId'));
        const targetImportId = Number(query.get('importId'));
        if (listId > 0 && versionId > 0) {
            openList({ listId, versionId }, targetImportId > 0 ? targetImportId : undefined);
        }
        // 页面首次加载只解析一次 hash，后续名单切换由页面操作驱动。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadListOptions = useCallback(async (listName?: string, ownerId?: number) => {
        setLoadingLists(true);
        try {
            const result = await v2Api.recipientLists({
                ownerId,
                listName: listName && listName.trim() ? listName.trim() : undefined,
                pageNo: 1,
                pageSize: 100,
            });
            setListOptions(result.items || []);
        } catch (nextError) {
            setError(asV2Error(nextError));
        } finally {
            setLoadingLists(false);
        }
    }, []);

    const loadOwnerOptions = useCallback(async () => {
        setLoadingOwners(true);
        setOwnerError(undefined);
        try {
            const response = await v2Api.recipientListOwners();
            const eligible = response.items || [];
            setOwnerOptions(eligible);
            setCanViewAllLists(response.canViewAll);
            setCurrentActorId(response.currentActorId);
            if (!createForm.getFieldValue('ownerId')) {
                createForm.setFieldsValue({ ownerId: response.currentActorId });
            }
            openForm.setFieldsValue({ ownerId: response.currentActorId, listKey: undefined });
            await loadListOptions(undefined, response.currentActorId);
            if (eligible.length === 0) setOwnerError('没有找到可用的负责人账号');
        } catch (nextError) {
            setOwnerError('负责人列表加载失败，请稍后重试');
        } finally {
            setLoadingOwners(false);
        }
    }, [createForm, loadListOptions, openForm]);

    useEffect(() => {
        loadOwnerOptions();
    }, [loadOwnerOptions]);

    useEffect(() => {
        if (current) loadMembers(current, memberFilters);
    }, [current, memberFilters, loadMembers]);

    const currentListId = current && current.listId;
    const currentVersionId = current && current.versionId;
    const importId = importSummary && importSummary.importId;
    const importStatus = importSummary && importSummary.status;

    useEffect(() => {
        if (!currentListId || !currentVersionId || !importId || !importStatus || !activeImportStates.includes(importStatus)) return;
        const timer = window.setInterval(async () => {
            if (document.hidden) return;
            try {
                const next = await v2Api.recipientImport(
                    currentListId,
                    currentVersionId,
                    importId
                );
                setImportSummary(next);
                if (!activeImportStates.includes(next.status)) {
                    setImportRows(
                        await v2Api.recipientImportRows(
                            currentListId,
                            currentVersionId,
                            next.importId,
                            { pageNo: 1, pageSize: emptyImportRows.pageSize }
                        )
                    );
                }
            } catch (nextError) {
                setImportError(capabilityMessage(asV2Error(nextError), 'Excel/CSV 导入'));
                window.clearInterval(timer);
            }
        }, 3000);
        return () => window.clearInterval(timer);
    }, [currentListId, currentVersionId, importId, importStatus]);

    const createList = async () => {
        const values = await createForm.validateFields();
        setSaving(true);
        try {
            const result = await v2Api.createRecipientList({
                listName: values.listName,
                description: values.description,
                businessDomainCode: values.businessDomainCode,
                ownerId: Number(values.ownerId),
                sourceType: 'MANUAL',
            });
            const next = { ...result, listName: values.listName, status: 'DRAFT' };
            setCreateVisible(false);
            createForm.resetFields();
            message.success('名单已创建');
            openList(next);
            loadListOptions(undefined, Number(values.ownerId));
        } catch (nextError) {
            message.error(asV2Error(nextError).message);
        } finally {
            setSaving(false);
        }
    };

    const openSelectedList = async (listKey?: string) => {
        if (!listKey) return;
        const [listId, versionId] = String(listKey).split(':').map(Number);
        const selected = listOptions.find((item) => item.listId === listId && item.versionId === versionId);
        await openList(selected || { listId, versionId });
    };

    const loadVersions = async () => {
        if (!current) return;
        setLoadingVersions(true);
        try {
            setVersions(await v2Api.recipientListVersions(current.listId));
            setHistoryVisible(true);
        } catch (nextError) {
            message.error(asV2Error(nextError).message);
        } finally {
            setLoadingVersions(false);
        }
    };

    const createNextVersion = async () => {
        if (!current) return;
        const values = await versionForm.validateFields();
        setSaving(true);
        try {
            const result = await v2Api.createRecipientListVersion(current.listId, {
                baseVersionId: current.versionId,
                reason: values.reason,
            });
            message.success(`V${result.versionNo} 已创建，成员资格已重新校验`);
            setCreateVersionVisible(false);
            versionForm.resetFields();
            const next = { ...current, ...result, status: 'READY', isCurrent: true };
            await openList(next);
            await loadListOptions(undefined, openForm.getFieldValue('ownerId'));
        } catch (nextError) {
            message.error(asV2Error(nextError).message);
        } finally {
            setSaving(false);
        }
    };

    const loadCompanies = async (pageNo = 1, pageSize = emptyCompanyPage.size, keyword = companyKeyword) => {
        setCompanyLoading(true);
        try {
            const request = {
                companyQueryParam: { name: keyword.trim() || undefined },
                pageNo,
                pageSize,
            };
            const response = canViewAllLists
                ? await CompanyService.getCompanyListInfo(request)
                : await CompanyService.getCompanyByLoginAccount(request);
            const page = response && response.data && response.data.companyInfoList;
            setCompanyPage(page || { ...emptyCompanyPage, current: pageNo, size: pageSize });
        } catch (nextError) {
            message.error('CRM 公司列表加载失败');
        } finally {
            setCompanyLoading(false);
        }
    };

    const loadContacts = async (company: ICompanyInfo, pageNo = 1, pageSize = emptyContactPage.size) => {
        setSelectedCompany(company);
        setContactLoading(true);
        try {
            const response = await CustomerService.getCustomerByCompanyId(company.id, { pageNo, pageSize });
            const page = response && response.data && response.data.customerInfoList;
            setContactPage(page || { ...emptyContactPage, current: pageNo, size: pageSize });
        } catch (nextError) {
            message.error('CRM 联系人列表加载失败');
        } finally {
            setContactLoading(false);
        }
    };

    const openCompanyPicker = () => {
        setCompanyPickerVisible(true);
        setSelectedCompany(null);
        setCompanyKeyword('');
        setSelectedCustomerIds([]);
        setCompanyPage(emptyCompanyPage);
        setContactPage(emptyContactPage);
        loadCompanies(1, emptyCompanyPage.size, '');
    };

    const addSelectedContacts = async () => {
        if (!current || selectedCustomerIds.length === 0) return;
        setSaving(true);
        try {
            const result = await v2Api.addRecipientListLegacyCustomers(
                current.listId,
                current.versionId,
                selectedCustomerIds.map(Number)
            );
            message.success(`已加入 ${result.inserted || 0} 人，跳过重复 ${result.duplicate || 0} 人`);
            setCompanyPickerVisible(false);
            setSelectedCustomerIds([]);
            openList(current);
        } catch (nextError) {
            message.error(asV2Error(nextError).message);
        } finally {
            setSaving(false);
        }
    };

    const excludeMember = async () => {
        if (!current || !editingMember) return;
        const values = await excludeForm.validateFields();
        setSaving(true);
        try {
            await v2Api.updateRecipientListMember(current.listId, current.versionId, editingMember.id, {
                eligibilityStatus: 'EXCLUDED',
                exclusionDetail: values.reason,
                reason: values.reason,
            });
            message.success('联系人已移至已排除名单');
            setEditingMember(null);
            excludeForm.resetFields();
            openList(current);
        } catch (nextError) {
            message.error(asV2Error(nextError).message);
        } finally {
            setSaving(false);
        }
    };

    const restoreMember = (row: RecipientListMember) => {
        if (!current) return;
        Modal.confirm({
            title: '恢复为可发送联系人？',
            content: '服务端会重新执行当前资格规则，仍不满足规则的联系人无法恢复。',
            okText: '确认恢复',
            onOk: async () => {
                await v2Api.updateRecipientListMember(current.listId, current.versionId, row.id, {
                    eligibilityStatus: 'ELIGIBLE',
                    reason: '用户手动恢复为可发送联系人',
                });
                message.success('联系人已恢复为可发送');
                await openList(current);
            },
        });
    };

    const removeMember = (row: RecipientListMember) => {
        if (!current) return;
        Modal.confirm({
            title: '从当前发送名单中移除？',
            content: row.personName || row.maskedAddressSnapshot,
            okText: '确认移除',
            okButtonProps: { danger: true },
            onOk: async () => {
                await v2Api.removeRecipientListMember(
                    current.listId,
                    current.versionId,
                    row.id,
                    '用户从发送名单中手动移除'
                );
                message.success('联系人已从名单中移除');
                await openList(current);
            },
        });
    };

    const uploadImport = async () => {
        if (!current || !importFile) {
            message.error('请先选择名单并选择 XLSX/CSV 文件');
            return;
        }
        setSaving(true);
        setImportError(null);
        try {
            const result = await v2Api.importRecipientEmails(
                current.listId,
                current.versionId,
                importFile
            );
            setImportSummary(result);
            if (!activeImportStates.includes(result.status)) {
                setImportRows(
                    await v2Api.recipientImportRows(
                        current.listId,
                        current.versionId,
                        result.importId,
                        { pageNo: 1, pageSize: emptyImportRows.pageSize }
                    )
                );
            }
            setImportFile(null);
            message.success('文件已上传并完成 CRM 联系人匹配');
        } catch (nextError) {
            setImportError(capabilityMessage(asV2Error(nextError), 'Excel/CSV 导入'));
        } finally {
            setSaving(false);
        }
    };

    const commitImport = async () => {
        if (!current || !importSummary) return;
        setSaving(true);
        try {
            await v2Api.commitRecipientImport(
                current.listId,
                current.versionId,
                importSummary.importId,
                { reason: '确认唯一匹配结果并加入名单' }
            );
            message.success('唯一匹配行已加入名单');
            openList(current);
        } catch (nextError) {
            message.error(capabilityMessage(asV2Error(nextError), '导入确认'));
        } finally {
            setSaving(false);
        }
    };

    const resolveImportRow = async () => {
        if (!current || !importSummary || !resolvingRow) return;
        const values = await resolveForm.validateFields();
        setSaving(true);
        try {
            await v2Api.resolveRecipientImportRow(
                current.listId,
                current.versionId,
                importSummary.importId,
                resolvingRow.id,
                {
                    companyId: Number(values.companyId),
                    personId: Number(values.personId),
                    employmentId: Number(values.employmentId),
                    contactMethodId: Number(values.contactMethodId),
                    version: Number(resolvingRow.version || 0),
                    reason: values.reason,
                }
            );
            message.success('匹配冲突已解决');
            setResolvingRow(null);
            resolveForm.resetFields();
            setImportRows(
                await v2Api.recipientImportRows(
                    current.listId,
                    current.versionId,
                    importSummary.importId,
                    { pageNo: importRows.pageNo, pageSize: importRows.pageSize }
                )
            );
            setImportSummary(
                await v2Api.recipientImport(
                    current.listId,
                    current.versionId,
                    importSummary.importId
                )
            );
        } catch (nextError) {
            message.error(capabilityMessage(asV2Error(nextError), '导入冲突治理'));
        } finally {
            setSaving(false);
        }
    };

    const freeze = async () => {
        if (!current || !current.contentHash) return;
        const values = await freezeForm.validateFields();
        setSaving(true);
        try {
            const next = await v2Api.freezeRecipientList(current.listId, current.versionId, {
                contentHash: current.contentHash,
                reason: values.reason,
            });
            message.success('名单版本已冻结，可用于创建发送批次');
            setFreezeVisible(false);
            freezeForm.resetFields();
            setCurrent({ ...current, ...next });
            openList(current);
        } catch (nextError) {
            message.error(asV2Error(nextError).message);
        } finally {
            setSaving(false);
        }
    };

    const unfreeze = () => {
        if (!current) return;
        Modal.confirm({
            title: `解冻 ${current.listName || '发送名单'} · V${current.versionNo || '?'}？`,
            content: '解冻后该版本将恢复为可编辑状态，可以继续增删联系人或重新导入名单。',
            okText: '确认解冻',
            icon: <UnlockOutlined />,
            onOk: async () => {
                setSaving(true);
                try {
                    const next = await v2Api.unfreezeRecipientList(
                        current.listId,
                        current.versionId,
                        '管理员从发送名单页面解冻版本'
                    );
                    message.success('名单版本已解冻');
                    setCurrent({ ...current, ...next });
                    await openList({ ...current, ...next });
                    await loadListOptions(undefined, openForm.getFieldValue('ownerId'));
                } catch (nextError) {
                    message.error(asV2Error(nextError).message);
                } finally {
                    setSaving(false);
                }
            },
        });
    };

    const isCurrentVersion = !!current
        && (current.isCurrent === true || Number(current.isCurrent) === 1);
    const canCreateNextVersion = !!current && isCurrentVersion && current.status === 'FROZEN';
    const createVersionDisabledReason = !isCurrentVersion
        ? '只能基于当前版本新建下一版本'
        : current?.status !== 'FROZEN'
        ? '当前版本尚未冻结，可以直接编辑；冻结后才能基于它新建版本'
        : '';
    const batchReferenceCount = Number(current?.batchReferenceCount || 0);
    const canUnfreeze = !!current && isCurrentVersion
        && current.status === 'FROZEN' && batchReferenceCount === 0;
    const unfreezeDisabledReason = !isCurrentVersion
        ? '历史版本必须保持不可变，不能解冻'
        : batchReferenceCount > 0
        ? `该版本已被 ${batchReferenceCount} 个发送任务引用，不能解冻`
        : '';

    const memberColumns = [
        { title: '邮箱', dataIndex: 'maskedAddressSnapshot', width: 190, render: (v: string) => v || '—' },
        {
            title: 'CRM 联系人',
            key: 'contact',
            width: 220,
            render: (_: any, row: RecipientListMember) => (
                <div>
                    <strong>{row.personName || `联系人 #${row.personId}`}</strong>
                    <span className="v2-subline">{row.companyName || `企业 #${row.companyId}`}</span>
                </div>
            ),
        },
        { title: '职位', dataIndex: 'positionTitle', width: 150, render: (v: string) => v || '—' },
        { title: '域名', dataIndex: 'recipientDomain', width: 160 },
        { title: '资格状态', dataIndex: 'eligibilityStatus', width: 110, render: (v: string) => <StatusTag value={v} /> },
        {
            title: '排除/复核原因',
            key: 'reason',
            render: (_: any, row: RecipientListMember) =>
                row.exclusionDetail || mailFieldLabel(row.exclusionCode),
        },
        {
            title: '操作',
            key: 'action',
            width: 190,
            fixed: 'right' as 'right',
            render: (_: any, row: RecipientListMember) => (
                <Space size={4}>
                    {row.eligibilityStatus === 'EXCLUDED' ? (
                        <Button size="small" disabled={current?.status === 'FROZEN'} onClick={() => restoreMember(row)}>
                            恢复可发送
                        </Button>
                    ) : (
                        <Button
                            size="small"
                            disabled={current?.status === 'FROZEN'}
                            onClick={() => {
                                excludeForm.resetFields();
                                setEditingMember(row);
                            }}
                        >
                            移至已排除
                        </Button>
                    )}
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={current?.status === 'FROZEN'}
                        onClick={() => removeMember(row)}
                    >
                        移除
                    </Button>
                </Space>
            ),
        },
    ];

    const importColumns = [
        { title: '行', dataIndex: 'rowNo', width: 70 },
        { title: '邮箱', dataIndex: 'maskedEmail', width: 190 },
        {
            title: '匹配联系人',
            key: 'matched',
            width: 230,
            render: (_: any, row: RecipientImportRow) =>
                [row.companyName, row.personName, row.department, row.positionTitle].filter(Boolean).join(' · ') || '—',
        },
        { title: '匹配状态', dataIndex: 'mailMatchStatus', width: 180, render: (v: string) => <StatusTag value={v} /> },
        { title: '说明', dataIndex: 'errorMessage', render: (v: string) => v || '—' },
        {
            title: '治理',
            key: 'action',
            width: 100,
            fixed: 'right' as 'right',
            render: (_: any, row: RecipientImportRow) => (
                <Button
                    size="small"
                    disabled={!['MULTIPLE_MATCHES', 'UNMATCHED', 'STALE_CONTACT'].includes(row.mailMatchStatus || '')}
                    onClick={() => {
                        resolveForm.resetFields();
                        setResolvingRow(row);
                    }}
                >
                    选择 CRM 联系人
                </Button>
            ),
        },
    ];

    return (
        <div className="v2-page mail-page">
            <PageHeader
                eyebrow="开发与培育 · 批量邮件"
                title="发送名单与 Excel 导入"
                description="Excel/CSV 中只接收邮箱地址；系统在服务端批量匹配 CRM，未匹配、歧义或受限制的地址不能发送。"
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            createForm.resetFields();
                            if (currentActorId) createForm.setFieldsValue({ ownerId: currentActorId });
                            setCreateVisible(true);
                            loadOwnerOptions();
                        }}
                    >
                        新建名单
                    </Button>
                }
            />
            <Card className="v2-filter-card" bordered={false}>
                <Form form={openForm} layout="inline">
                    <Form.Item name="ownerId" label="负责人">
                        <Select
                            allowClear={canViewAllLists}
                            disabled={!canViewAllLists}
                            loading={loadingOwners}
                            style={{ width: 220 }}
                            placeholder="全部负责人"
                            onChange={(ownerId: number | undefined) => {
                                openForm.setFieldsValue({ listKey: undefined });
                                setCurrent(null);
                                loadListOptions(undefined, ownerId);
                            }}
                        >
                            {ownerOptions.map((owner) => (
                                <Select.Option key={owner.id} value={owner.id}>
                                    {owner.realName || owner.account}（{owner.account}）
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="listKey" label="名单名称" rules={[{ required: true, message: '请选择名单' }]}>
                        <Select
                            showSearch
                            filterOption={false}
                            loading={loadingLists}
                            style={{ width: 340 }}
                            placeholder="输入名单名称模糊查询"
                            onChange={(listKey: string) => openSelectedList(listKey)}
                            onSearch={(value) => {
                                if (listSearchTimer.current) window.clearTimeout(listSearchTimer.current);
                                listSearchTimer.current = window.setTimeout(() => {
                                    loadListOptions(value, openForm.getFieldValue('ownerId'));
                                }, 300);
                            }}
                        >
                            {listOptions.map((item) => (
                                <Select.Option key={`${item.listId}:${item.versionId}`} value={`${item.listId}:${item.versionId}`}>
                                    {item.listName} · V{item.versionNo || '?'} · {recipientVersionStatusLabel(item.status)}
                                    {item.isCurrent ? ' · 当前版本' : ' · 历史版本'}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Card>

            {current && (
                <>
                    <Row gutter={12} className="v2-stat-grid">
                        {[
                            ['总行数', current.totalCount || 0],
                            ['可发送', current.eligibleCount || 0],
                            ['已排除', current.excludedCount || 0],
                            ['待复核', current.reviewCount || 0],
                        ].map(([label, value]) => (
                            <Col xs={12} md={6} key={String(label)}>
                                <Card className="v2-stat-card"><Statistic title={label} value={value} /></Card>
                            </Col>
                        ))}
                    </Row>
                    <Card
                        className="v2-panel"
                        bordered={false}
                        title={
                            <Space>
                                <span>{current.listName || `名单 #${current.listId}`}</span>
                                <Tag>{`V${current.versionNo || '?'}`}</Tag>
                                <Tag>{recipientVersionStatusLabel(current.status)}</Tag>
                            </Space>
                        }
                        extra={
                            <Space>
                                <Button icon={<ReloadOutlined />} onClick={() => openList(current)}>刷新</Button>
                                <Button icon={<HistoryOutlined />} loading={loadingVersions} onClick={loadVersions}>
                                    历史版本
                                </Button>
                                <Tooltip title={createVersionDisabledReason}>
                                    <span>
                                        <Button
                                            disabled={!canCreateNextVersion}
                                            onClick={() => {
                                                versionForm.resetFields();
                                                setCreateVersionVisible(true);
                                            }}
                                        >
                                            基于 V{current.versionNo || '?'} 新建版本
                                        </Button>
                                    </span>
                                </Tooltip>
                                {current.status === 'FROZEN' && (
                                    <Tooltip title={unfreezeDisabledReason}>
                                        <span>
                                            <Button
                                                icon={<UnlockOutlined />}
                                                disabled={!canUnfreeze}
                                                loading={saving}
                                                onClick={unfreeze}
                                            >
                                                解冻版本
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                <Button disabled={current.status === 'FROZEN'} onClick={openCompanyPicker}>添加 CRM 联系人</Button>
                                <Button
                                    type="primary"
                                    disabled={current.status !== 'READY' || !current.contentHash || Number(current.reviewCount) > 0}
                                    onClick={() => setFreezeVisible(true)}
                                >
                                    冻结版本
                                </Button>
                            </Space>
                        }
                    >
                        <Space className="mail-list-meta v2-wrap-space">
                            <span>名单 ID：{current.listId}</span>
                            <span>当前显示：V{current.versionNo || '?'}</span>
                            <span>来源：{mailFieldLabel(current.sourceType || 'MANUAL', '未知来源')}</span>
                        </Space>
                    </Card>

                    <Card className="v2-panel v2-section" bordered={false} title="Excel/CSV 邮箱导入">
                        <Space className="mail-upload-row v2-wrap-space">
                            <Button icon={<DownloadOutlined />} onClick={downloadEmailImportTemplate}>
                                下载 CSV 模板
                            </Button>
                            <Upload
                                accept=".xlsx,.csv"
                                beforeUpload={(nextFile) => {
                                    setImportFile(nextFile as File);
                                    return false;
                                }}
                                onRemove={() => {
                                    setImportFile(null);
                                    return true;
                                }}
                            >
                                <Button icon={<FileExcelOutlined />}>选择名单文件</Button>
                            </Upload>
                            <Button type="primary" loading={saving} disabled={!importFile || current.status === 'FROZEN'} onClick={uploadImport}>
                                上传并匹配 CRM
                            </Button>
                            {importSummary && <Tag>{recipientImportStatusLabel(importSummary.status)}</Tag>}
                        </Space>
                        {importError && <Alert className="mail-inline-alert" type="warning" showIcon message={importError} />}
                        {importSummary && (
                            <>
                                <Row gutter={12} className="mail-import-summary">
                                    {[
                                        ['唯一匹配', importSummary.matchedCount || 0],
                                        ['多匹配', importSummary.multipleMatchesCount || 0],
                                        ['未匹配', importSummary.unmatchedCount || 0],
                                        ['非法/重复', (importSummary.invalidCount || 0) + (importSummary.duplicateCount || 0)],
                                    ].map(([label, value]) => (
                                        <Col xs={12} md={6} key={String(label)}><Statistic title={label} value={value} /></Col>
                                    ))}
                                </Row>
                                <Table
                                    rowKey="id"
                                    size="small"
                                    columns={importColumns}
                                    dataSource={importRows.items}
                                    pagination={{
                                        current: importRows.pageNo,
                                        pageSize: importRows.pageSize,
                                        total: importRows.total,
                                        showSizeChanger: true,
                                        pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                                        showTotal: (total) => `共 ${total} 条导入记录`,
                                        onChange: async (pageNo, pageSize) => {
                                            rememberMailPageSize('recipient-import-rows', pageSize);
                                            try {
                                                setImportRows(
                                                    await v2Api.recipientImportRows(
                                                        current.listId,
                                                        current.versionId,
                                                        importSummary.importId,
                                                        { pageNo, pageSize }
                                                    )
                                                );
                                            } catch (nextError) {
                                                setImportError(capabilityMessage(asV2Error(nextError), '导入结果分页'));
                                            }
                                        },
                                    }}
                                    scroll={{ x: 900 }}
                                />
                                <Button
                                    className="mail-commit-button"
                                    type="primary"
                                    disabled={!['PARSED', 'PRECHECKED', 'PARTIAL'].includes(importSummary.status)}
                                    loading={saving}
                                    onClick={commitImport}
                                >
                                    确认唯一匹配结果并加入名单
                                </Button>
                            </>
                        )}
                    </Card>

                    <Card className="v2-panel v2-section" bordered={false} title="名单成员">
                        <Space className="mail-table-filter">
                            <Select
                                allowClear
                                placeholder="资格状态"
                                style={{ width: 160 }}
                                value={memberFilters.eligibilityStatus}
                                onChange={(eligibilityStatus) =>
                                    setMemberFilters({ ...memberFilters, eligibilityStatus, pageNo: 1 })
                                }
                            >
                                <Select.Option value="ELIGIBLE">可发送</Select.Option>
                                <Select.Option value="EXCLUDED">已排除</Select.Option>
                                <Select.Option value="REVIEW">待复核</Select.Option>
                            </Select>
                        </Space>
                        <RequestState loading={loading} error={error} empty={!members.items.length} onRetry={() => openList(current)}>
                            <Table
                                rowKey="id"
                                columns={memberColumns}
                                dataSource={members.items}
                                scroll={{ x: 1100 }}
                                pagination={{
                                    current: members.pageNo,
                                    pageSize: members.pageSize,
                                    total: members.total,
                                    showSizeChanger: true,
                                    pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                                    showTotal: (total) => `共 ${total} 个名单成员`,
                                    onChange: (pageNo, pageSize) => {
                                        rememberMailPageSize('recipient-members', pageSize);
                                        setMemberFilters({ ...memberFilters, pageNo, pageSize });
                                    },
                                }}
                            />
                        </RequestState>
                    </Card>
                </>
            )}

            {!current && error && <RequestState loading={loading} error={error} empty={false} onRetry={() => undefined}><span /></RequestState>}

            <Modal title="新建发送名单" visible={createVisible} onOk={createList} confirmLoading={saving} onCancel={() => setCreateVisible(false)}>
                <Form form={createForm} layout="vertical">
                    <Form.Item name="listName" label="名单名称" rules={[{ required: true, whitespace: true }]}><Input maxLength={200} /></Form.Item>
                    <Form.Item name="description" label="用途说明"><Input.TextArea rows={3} maxLength={1000} /></Form.Item>
                    <Form.Item name="businessDomainCode" label="业务域编码"><Input maxLength={40} /></Form.Item>
                    <Form.Item name="ownerId" label="负责人" rules={[{ required: true, message: '请选择负责人' }]}>
                        <Select
                            showSearch
                            allowClear
                            loading={loadingOwners}
                            optionFilterProp="children"
                            placeholder="请选择销售或管理员"
                            notFoundContent={loadingOwners ? '正在加载…' : '暂无可选负责人'}
                        >
                            {ownerOptions.map((owner) => (
                                <Select.Option key={owner.id} value={owner.id}>
                                    {`${owner.realName || owner.account}（${owner.account}）${owner.roleCodes ? ` · ${owner.roleCodes}` : ''}`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {ownerError && (
                        <Alert
                            type="warning"
                            showIcon
                            message={ownerError}
                            description={<Button size="small" onClick={loadOwnerOptions}>重新加载</Button>}
                        />
                    )}
                </Form>
            </Modal>

            <Modal
                title={selectedCompany ? `选择联系人 · ${selectedCompany.name}` : '选择 CRM 公司'}
                visible={companyPickerVisible}
                onOk={addSelectedContacts}
                okText={`添加已选联系人（${selectedCustomerIds.length}）`}
                okButtonProps={{ disabled: !selectedCompany || selectedCustomerIds.length === 0 }}
                confirmLoading={saving}
                onCancel={() => setCompanyPickerVisible(false)}
                width={1180}
                destroyOnClose
            >
                {!selectedCompany ? (
                    <>
                        <Space style={{ marginBottom: 16 }}>
                            <Input.Search
                                allowClear
                                value={companyKeyword}
                                placeholder="输入公司名称查询"
                                style={{ width: 320 }}
                                onChange={(event) => setCompanyKeyword(event.target.value)}
                                onSearch={(value) => loadCompanies(1, companyPage.size || 10, value)}
                            />
                        </Space>
                        <Table
                            rowKey="id"
                            size="small"
                            loading={companyLoading}
                            dataSource={companyPage.records}
                            columns={[
                                {
                                    title: '公司名', dataIndex: 'name', width: 190,
                                    render: (name: string, row: ICompanyInfo) => (
                                        <Button type="link" style={{ padding: 0 }} onClick={() => loadContacts(row)}>{name}</Button>
                                    ),
                                },
                                { title: '销售', dataIndex: 'salesName', width: 100 },
                                { title: '性质', dataIndex: 'nature', width: 100 },
                                { title: '总机', dataIndex: 'phone', width: 130 },
                                { title: '省份', dataIndex: 'province', width: 90 },
                                { title: '地址', dataIndex: 'address', width: 230 },
                                { title: '行业', dataIndex: 'industry', width: 130 },
                                { title: '状态', dataIndex: 'states', width: 80, render: (value: number) => value === 0 ? '正常' : '无效' },
                            ]}
                            scroll={{ x: 1050 }}
                            pagination={{
                                current: companyPage.current,
                                pageSize: companyPage.size || emptyCompanyPage.size,
                                total: companyPage.total,
                                showSizeChanger: true,
                                pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                                showTotal: (total) => `共 ${total} 家公司`,
                                onChange: (pageNo, pageSize) => {
                                    rememberMailPageSize('recipient-companies', pageSize);
                                    loadCompanies(pageNo, pageSize, companyKeyword);
                                },
                            }}
                        />
                    </>
                ) : (
                    <>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            style={{ marginBottom: 16 }}
                            onClick={() => {
                                setSelectedCompany(null);
                                setContactPage(emptyContactPage);
                            }}
                        >
                            返回公司列表
                        </Button>
                        <Table
                            rowKey="id"
                            size="small"
                            loading={contactLoading}
                            dataSource={contactPage.records}
                            rowSelection={{
                                selectedRowKeys: selectedCustomerIds,
                                preserveSelectedRowKeys: true,
                                onChange: (keys) => setSelectedCustomerIds(keys),
                                getCheckboxProps: (row: ICustomerInfo) => ({
                                    disabled: !row.email || !String(row.email).trim(),
                                    name: row.cname,
                                }),
                            }}
                            columns={[
                                { title: '重点', dataIndex: 'keyCustomer', width: 70, render: (value: boolean) => value ? '是' : '否' },
                                { title: '姓名', dataIndex: 'cname', width: 100 },
                                { title: '英文名', dataIndex: 'ename', width: 100 },
                                { title: '性别', dataIndex: 'gender', width: 60, render: (value: number) => value === 1 ? '男' : value === 2 ? '女' : '—' },
                                { title: '手机号', dataIndex: 'mobile', width: 130 },
                                { title: '固话', dataIndex: 'phone', width: 130 },
                                { title: '职位', dataIndex: 'position', width: 130 },
                                { title: '部门', dataIndex: 'department', width: 120 },
                                { title: '邮箱', dataIndex: 'email', width: 230, render: (value: string) => value || <Tag color="red">未配置</Tag> },
                                { title: '微信', dataIndex: 'wechat', width: 120 },
                                { title: '备注信息', dataIndex: 'remark', width: 180 },
                            ]}
                            scroll={{ x: 1370 }}
                            pagination={{
                                current: contactPage.current,
                                pageSize: contactPage.size || emptyContactPage.size,
                                total: contactPage.total,
                                showSizeChanger: true,
                                pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                                showTotal: (total) => `共 ${total} 个联系人`,
                                onChange: (pageNo, pageSize) => {
                                    rememberMailPageSize('recipient-contacts', pageSize);
                                    loadContacts(selectedCompany, pageNo, pageSize);
                                },
                            }}
                        />
                    </>
                )}
            </Modal>

            <Modal
                title="移至已排除名单"
                visible={!!editingMember}
                onOk={excludeMember}
                confirmLoading={saving}
                okText="确认排除"
                onCancel={() => setEditingMember(null)}
            >
                <Form form={excludeForm} layout="vertical">
                    <Form.Item label="联系人">
                        <Input value={editingMember?.personName || editingMember?.maskedAddressSnapshot} disabled />
                    </Form.Item>
                    <Form.Item name="reason" label="排除原因" rules={[{ required: true, whitespace: true }]}>
                        <Input.TextArea rows={3} maxLength={500} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`${current?.listName || '发送名单'} · 历史版本`}
                visible={historyVisible}
                footer={null}
                onCancel={() => setHistoryVisible(false)}
                width={900}
            >
                <Table
                    rowKey="versionId"
                    size="small"
                    loading={loadingVersions}
                    dataSource={versions}
                    pagination={false}
                    columns={[
                        {
                            title: '版本', dataIndex: 'versionNo', width: 90,
                            render: (value: number, row: RecipientListVersion) => (
                                <Space><strong>V{value}</strong>{row.isCurrent ? <Tag color="blue">当前</Tag> : null}</Space>
                            ),
                        },
                        {
                            title: '状态',
                            dataIndex: 'status',
                            width: 110,
                            render: (value: string) => <Tag>{recipientVersionStatusLabel(value)}</Tag>,
                        },
                        { title: '总人数', dataIndex: 'totalCount', width: 90 },
                        { title: '可发送', dataIndex: 'eligibleCount', width: 90 },
                        { title: '已排除', dataIndex: 'excludedCount', width: 90 },
                        { title: '创建时间', dataIndex: 'createdAt', width: 170, render: formatDateTime },
                        {
                            title: '查看', key: 'view', width: 80,
                            render: (_: any, row: RecipientListVersion) => (
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setHistoryVisible(false);
                                        openList({
                                            listId: current!.listId,
                                            listName: current!.listName,
                                            versionId: row.versionId,
                                            versionNo: row.versionNo,
                                            status: row.status,
                                        });
                                    }}
                                >
                                    查看
                                </Button>
                            ),
                        },
                    ]}
                />
            </Modal>

            <Modal
                title={`基于 V${current?.versionNo || '?'} 新建版本`}
                visible={createVersionVisible}
                onOk={createNextVersion}
                confirmLoading={saving}
                okText="创建新版本"
                onCancel={() => setCreateVersionVisible(false)}
            >
                <Alert
                    type="info"
                    showIcon
                    message="新版本会复制当前成员，并重新执行发送资格检查"
                    description="原冻结版本及已绑定的发送任务不会变化；手动排除的联系人会继续保持排除。"
                />
                <Form form={versionForm} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item name="reason" label="新建原因" rules={[{ required: true, whitespace: true }]}>
                        <Input.TextArea rows={3} maxLength={500} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title="冻结名单版本" visible={freezeVisible} onOk={freeze} confirmLoading={saving} onCancel={() => setFreezeVisible(false)} okText="确认冻结">
                <Alert type="warning" showIcon message="冻结后成员不可增删；修改名单需要创建新版本。" />
                <Form form={freezeForm} layout="vertical">
                    <Form.Item name="reason" label="冻结理由" rules={[{ required: true, whitespace: true }]}><Input.TextArea rows={3} /></Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`解决导入匹配 · 第 ${resolvingRow?.rowNo || '—'} 行`}
                visible={!!resolvingRow}
                onOk={resolveImportRow}
                confirmLoading={saving}
                onCancel={() => setResolvingRow(null)}
                width={660}
            >
                <Alert
                    type="warning"
                    showIcon
                    message="只能选择 CRM 已存在且关系一致的企业、联系人、当前任职和 EMAIL 联系方式"
                    description="页面不会直接修改导入邮箱或新建外部联系人；服务端会再次校验邮箱 HMAC 与触达政策。"
                />
                <Form form={resolveForm} layout="vertical">
                    <Row gutter={12}>
                        <Col span={12}><Form.Item name="companyId" label="企业 ID" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item></Col>
                        <Col span={12}><Form.Item name="personId" label="联系人 ID" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item></Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}><Form.Item name="employmentId" label="任职 ID" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item></Col>
                        <Col span={12}><Form.Item name="contactMethodId" label="邮箱联系方式 ID" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item></Col>
                    </Row>
                    <Form.Item name="reason" label="治理依据" rules={[{ required: true, whitespace: true }]}><Input.TextArea rows={3} /></Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default RecipientListsPage;
